import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Customer, CreateCustomerDto, UpdateCustomerDto } from '../models/customer.model';
import { OnboardingService } from './onboarding.service';
import { OrganizationContextService } from './organization-context.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * Customer Service
 *
 * Handles customer management with real API integration.
 * Uses signal-based state management for reactive UI updates.
 *
 * Reference: /agent-os/specs/2026-01-07-customers-fields-backend/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private http = inject(HttpClient);
  private onboardingService = inject(OnboardingService);
  private organizationContext = inject(OrganizationContextService);
  private apiUrl = environment.apiUrl;

  // Signal-based state management
  private customersSignal = signal<Customer[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly customers = computed(() => this.customersSignal().filter(c => !c.deletedAt));
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    // React to organization context changes — load customers when org becomes available
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        this.loadCustomers();
      }
    });
  }

  /**
   * Load customers from API
   */
  async loadCustomers(search?: string, page: number = 1, limit: number = 100): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = { page, limit };
      if (search) {
        params.search = search;
      }

      const response = await firstValueFrom(
        this.http.get<ApiResponse<Customer[]>>(`${this.apiUrl}/customers`, { params })
      );

      if (response.success && response.data) {
        this.customersSignal.set(response.data);
      } else {
        throw new Error(response.error || 'Failed to load customers');
      }
    } catch (error: any) {
      console.error('Error loading customers:', error);
      this.errorSignal.set(error.message || 'Failed to load customers');
      // Keep existing data on error
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get all customers (from signal)
   */
  getCustomers(): Customer[] {
    return this.customers();
  }

  /**
   * Get customer by ID
   */
  getCustomerById(id: string): Customer | undefined {
    return this.customersSignal().find(c => c.id === id && !c.deletedAt);
  }

  /**
   * Get customer by ID from API
   */
  async fetchCustomerById(id: string): Promise<Customer | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<Customer>>(`${this.apiUrl}/customers/${id}`)
      );

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching customer:', error);
      this.errorSignal.set(error.message || 'Failed to fetch customer');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Create a new customer
   */
  async createCustomer(dto: CreateCustomerDto): Promise<Customer | null> {
    const isFirstCustomer = this.customers().length === 0;
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<Customer>>(`${this.apiUrl}/customers`, dto)
      );

      if (response.success && response.data) {
        // Add to local state
        this.customersSignal.update(customers => [...customers, response.data!]);

        // Mark onboarding checklist item as complete if this is the first customer
        if (isFirstCustomer) {
          this.onboardingService.completeItem('add_customers');
        }

        return response.data;
      }

      throw new Error(response.error || 'Failed to create customer');
    } catch (error: any) {
      console.error('Error creating customer:', error);
      this.errorSignal.set(error.message || 'Failed to create customer');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update an existing customer
   */
  async updateCustomer(id: string, dto: UpdateCustomerDto): Promise<Customer | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<Customer>>(`${this.apiUrl}/customers/${id}`, dto)
      );

      if (response.success && response.data) {
        // Update local state
        this.customersSignal.update(customers => {
          const index = customers.findIndex(c => c.id === id);
          if (index !== -1) {
            const newCustomers = [...customers];
            newCustomers[index] = response.data!;
            return newCustomers;
          }
          return customers;
        });

        return response.data;
      }

      throw new Error(response.error || 'Failed to update customer');
    } catch (error: any) {
      console.error('Error updating customer:', error);
      this.errorSignal.set(error.message || 'Failed to update customer');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Delete (soft-delete) a customer
   */
  async deleteCustomer(id: string): Promise<boolean> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(`${this.apiUrl}/customers/${id}`)
      );

      if (response.success) {
        // Update local state (soft delete)
        this.customersSignal.update(customers => {
          const index = customers.findIndex(c => c.id === id);
          if (index !== -1) {
            const newCustomers = [...customers];
            newCustomers[index] = {
              ...newCustomers[index],
              deletedAt: new Date().toISOString()
            };
            return newCustomers;
          }
          return customers;
        });

        return true;
      }

      throw new Error(response.error || 'Failed to delete customer');
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      this.errorSignal.set(error.message || 'Failed to delete customer');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get customer options for dropdowns
   */
  getCustomerOptions(): { id: string; name: string }[] {
    return this.customers().map(c => ({
      id: c.id,
      name: c.name
    }));
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
